import { BrowserRouter, Route, useParams, Routes, Navigate } from "react-router-dom";
import { HomeScreen } from "src/screens/[home].screen";
import { SignedOutScreen } from "src/screens/[signed-out].screen";
import { SetCurrencySymbolScreen } from "src/screens/[set-currency-symbol].screen";
import { DevFlagsScreen } from "src/screens/[dev-flags].screen";
import { AdminScreen } from "src/screens/[admin].screen";
import { CostTrackingScreen } from "src/screens/[cost-tracking].screen";
import { CostTrackingDetailScreen } from "src/screens/[cost-tracking-detail].screen";
import { CostEngineCompareScreen } from "src/screens/[cost-engine-compare].screen";
import { CostEditorScreen } from "src/screens/[cost-editor].screen";
import { ModuleExplorerScreen } from "src/screens/[module-explorer].screen";
import { ComponentExplorerScreen } from "src/screens/[component-explorer].screen";
import { TessellationStepsScreen } from "src/screens/[tessellation-steps].screen";
import { LanguageProvider } from "src/translations";
import { observer } from "mobx-react-lite";
import { usePosthog } from "src/contexts/posthog.context";
import { FLAGS } from "src/posthog/flags";
import { Editor } from "src/types/Editor";
import { useApi } from "src/contexts/api.context";
import { useAuth } from "src/contexts/auth.context";
import { useEnv } from "src/contexts/env.context";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  SnapshotService,
  useSnapshotService,
} from "src/services/useSnapshot.service/useSnapshot.service";
import { Navigator } from "src/components/Navigator/Navigator";
import { DesignPreview } from "./DesignPreview";
import { DesignEditor } from "./DesignEditor";
import { StudioApp } from "./Studio/StudioApp";
import { StudioCheckoutReturn } from "./Studio/StudioCheckoutReturn";
import { LoadingScreen } from "./LoadingScreen";
import { useWorkspaceQuery } from "src/hooks/useWorkspaceQuery";
import { useActiveParams } from "src/hooks/useActiveParams";

const Nav = (props: { to: string }) => {
  const params = useParams();
  const to = useMemo(() => {
    const map = new Map(Object.keys(params).map((key) => [`:${key}`, params[key] as string]));
    let path = props.to;
    for (const [key, value] of map) {
      path = path.replace(key, value);
    }
    return path;
  }, [params, props.to]);
  return <Navigate to={to} replace />;
};

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  // Show the loading screen for both "still resolving the cookie" and
  // "401 returned, browser is mid-redirect to /api/auth/login" — both
  // states have no user but a render is imminent.
  if (isLoading || !user) return <LoadingScreen />;
  return <>{children}</>;
}

// Gated wrapper for the tessellation-steps admin route. Hides the screen
// (and the Daiwa typology footprints) from production unless the flag is on.
//
// We subscribe to posthog directly rather than going through useFeatureFlag
// because that hook starts at `false` on first render and updates via
// useEffect — fine for "render null vs render button" gates, but our
// <Navigate> fires during first render, so we'd bounce legitimate users
// before the real flag value arrives. Wait for the first onFeatureFlags
// callback before deciding.
const TessellationStepsGated = observer(function TessellationStepsGated() {
  const posthog = usePosthog();
  const [state, setState] = useState<"pending" | "on" | "off">("pending");
  useEffect(() => {
    return posthog.onFeatureFlags(() => {
      setState(posthog.isFlagEnabled(FLAGS.tessellationSteps) ? "on" : "off");
    });
  }, [posthog]);
  if (state === "pending") return null;
  if (state === "off") return <Navigate to="/home" replace />;
  return <TessellationStepsScreen />;
});

// Gates the Studio flow. Waits for onFeatureFlags so the <Navigate> fallback
// doesn't bounce legitimate users before the real flag value arrives. Takes
// `editor` because Studio binds to the real Editor for generation + persistence.
const StudioRouteGated = observer(function StudioRouteGated({ editor }: { editor: Editor }) {
  const posthog = usePosthog();
  const [state, setState] = useState<"pending" | "on" | "off">("pending");
  useEffect(() => {
    return posthog.onFeatureFlags(() => {
      setState(posthog.isFlagEnabled(FLAGS.designStudio) ? "on" : "off");
    });
  }, [posthog]);
  if (state === "pending") return null;
  if (state === "off") return <Navigate to="canvas" replace />;
  return <StudioApp editor={editor} />;
});

export const Router = observer(() => {
  const env = useEnv();
  const isDev = env.authMode !== "prod";
  return (
    <LanguageProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" Component={SignedOutScreen} />
          <Route
            path="*"
            element={
              <RequireAuth>
                <Routes>
                  <Route path="/home" Component={HomeScreen} />
                  <Route path="/admin" Component={AdminScreen} />
                  <Route path="/set-currency-symbol" Component={SetCurrencySymbolScreen} />
                  {/* Dev-only flag toggle page — never registered in prod. */}
                  {isDev && <Route path="/dev/flags" Component={DevFlagsScreen} />}
                  <Route path="/admin/cost-tracking" Component={CostTrackingScreen} />
                  <Route path="/admin/module-explorer" Component={ModuleExplorerScreen} />
                  <Route path="/admin/component-explorer" Component={ComponentExplorerScreen} />
                  <Route path="/admin/tessellation-steps" Component={TessellationStepsGated} />
                  <Route
                    path="/admin/cost-tracking/:variantIndex"
                    Component={CostTrackingDetailScreen}
                  />
                  <Route
                    path="/admin/cost-engine-compare/:variantIndex"
                    Component={CostEngineCompareScreen}
                  />
                  {isDev && (
                    <Route path="/dev/cost-editor/:fixtureId" Component={CostEditorScreen} />
                  )}
                  {/* Stripe report payment links redirect here after checkout. */}
                  <Route path="/checkout/return" element={<StudioCheckoutReturn />} />
                  <Route path="/projects/:projectId/*" element={<ProjectRouter />} />
                  <Route path="/projects" element={<Nav to="/home" />} />
                  <Route path="/" element={<Nav to="/home" />} />
                  <Route path="*" element={<Nav to="/home" />} />
                </Routes>
              </RequireAuth>
            }
          />
        </Routes>
      </BrowserRouter>
    </LanguageProvider>
  );
});

const ProjectRouter = observer(() => {
  const { projectId, scenarioId } = useActiveParams();
  const api = useApi();
  const [editor, setEditor] = useState<Editor | null>(null);
  const snapshot = useSnapshotService();
  const workspace = useWorkspaceQuery();
  useEffect(() => {
    if (!projectId || !scenarioId) return;
    const editor = new Editor(api, projectId, scenarioId);
    setEditor(editor);
    return () => editor.destory();
  }, [api, projectId, scenarioId]);

  if (!editor) return null;
  if (!workspace.data) return null;

  return (
    <SnapshotService.Provider value={snapshot}>
      <Routes>
        {/* Studio owns the whole screen — its own light shell, no Navigator
            and no dark app chrome. Everything else keeps the existing shell. */}
        <Route path="studio" element={<StudioRouteGated editor={editor} />} />
        <Route
          path="*"
          element={
            <div className="overflow-hidden w-screen h-screen flex flex-row bg-elevation-3 text-gray-200 text-sm">
              <Navigator editor={editor} />
              <div className="flex flex-1 overflow-hidden">
                <Routes>
                  <Route
                    path="canvas"
                    element={<DesignEditor editor={editor} workspace={workspace.data} />}
                  />
                  <Route path="designs/:designId" element={<DesignPreview />} />
                  <Route path="*" element={<Nav to="canvas" />} />
                </Routes>
              </div>
            </div>
          }
        />
      </Routes>
    </SnapshotService.Provider>
  );
});
